// Starting a draft from the PBJ screens (a PBJ node's output or a template):
// the draft is a chat conversation, so on success, and on a failure that
// still stored the conversation (Coba lagi answers there), it opens that
// conversation, inside the project when there is one. One draft at a time;
// `busy` names the key of the one running, `error` the one that failed.
import { useState } from "react";
import * as API from "../api";
import { navigate } from "../router";
import { conversationPath } from "../screens/Chat";
import { useChatShell } from "../shell/chat-context";
import type { FeatureState } from "../shell/features";

/** /templates drafting needs the template library, the chat and document generation. */
export function canDraft(features: FeatureState): boolean {
  return features.has("templates") && features.has("chat") && features.has("document_generation");
}

export interface DraftState {
  busy: string | null;
  error: { key: string; message: string } | null;
  start: (key: string, call: () => Promise<API.TemplateDraft>, projectId?: string) => Promise<void>;
}

export function useDraft(): DraftState {
  const shell = useChatShell();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<DraftState["error"]>(null);

  const start: DraftState["start"] = async (key, call, projectId) => {
    if (busy) return;
    setBusy(key);
    setError(null);
    try {
      const draft = await call();
      shell?.bumpHistory();
      navigate(conversationPath(draft.conversationId, draft.projectId ?? projectId));
    } catch (err) {
      if (err instanceof API.ApiError && err.conversationId) {
        shell?.bumpHistory();
        navigate(conversationPath(err.conversationId, projectId));
      } else {
        setError({ key, message: API.errorMessage(err) });
      }
    } finally {
      // The project page stays mounted under its conversation route.
      setBusy(null);
    }
  };

  return { busy, error, start };
}
